package diana.soleil.design.iraniancanadienbazzar.core.common

import com.russhwolf.settings.Settings

/**
 * Typed, persisted app preferences over [multiplatform-settings] (SharedPreferences on Android,
 * NSUserDefaults on iOS, localStorage on web). Stores small primitives only — the language code and
 * active metro id — so the user's choices survive restarts. Values are raw strings; callers map to/from
 * domain types (e.g. `Language.fromCode`). `null` clears the key.
 */
class AppPreferences(private val settings: Settings = Settings()) {

    var languageCode: String?
        get() = settings.getStringOrNull(KEY_LANGUAGE)
        set(value) = if (value == null) settings.remove(KEY_LANGUAGE) else settings.putString(KEY_LANGUAGE, value)

    /**
     * Light/dark preference: `system` (default) / `light` / `dark`. Device-local by design — someone
     * who wants dark on their phone at night does not necessarily want it on the shared desktop, so
     * this deliberately does NOT mirror to `users/{uid}` the way language does. Callers map via
     * `AppearanceMode.fromCode`.
     */
    var appearanceCode: String?
        get() = settings.getStringOrNull(KEY_APPEARANCE)
        set(value) = if (value == null) settings.remove(KEY_APPEARANCE) else settings.putString(KEY_APPEARANCE, value)

    /** Active metro id; `null`/blank means "All \<country\>". */
    var metroId: String?
        get() = settings.getStringOrNull(KEY_METRO)?.ifBlank { null }
        set(value) = if (value.isNullOrBlank()) settings.remove(KEY_METRO) else settings.putString(KEY_METRO, value)

    /**
     * Active country code ("CA"/"US"), the first-class country filter dimension (docs/US_EXPANSION_PLAN.md).
     * `null` = never chosen; callers default it from the device region on first run. A selected metro
     * implies its country, so this mainly holds the choice while browsing "All \<country\>".
     */
    var countryCode: String?
        get() = settings.getStringOrNull(KEY_COUNTRY)?.ifBlank { null }
        set(value) = if (value.isNullOrBlank()) settings.remove(KEY_COUNTRY) else settings.putString(KEY_COUNTRY, value)

    /**
     * Locally saved (favorited) business ids, stored comma-joined. Firestore doc ids never contain
     * commas, so the delimiter is safe. Migrates to `users/{uid}.savedBusinesses` when signed-in.
     */
    var favoriteBusinessIds: Set<String>
        get() = settings.getStringOrNull(KEY_FAVORITES)
            ?.split(',')?.filter { it.isNotBlank() }?.toSet() ?: emptySet()
        set(value) = if (value.isEmpty()) settings.remove(KEY_FAVORITES) else settings.putString(KEY_FAVORITES, value.joinToString(","))

    /**
     * Locally saved (bookmarked) news article ids, comma-joined (ids never contain commas). Unlike
     * marketplace saves this DOES mirror to `users/{uid}.savedNewsIds` — news is curated, long-lived
     * content, so a reading list should follow the account across devices the way favorites do.
     */
    var savedNewsIds: Set<String>
        get() = settings.getStringOrNull(KEY_SAVED_NEWS)
            ?.split(',')?.filter { it.isNotBlank() }?.toSet() ?: emptySet()
        set(value) = if (value.isEmpty()) settings.remove(KEY_SAVED_NEWS) else settings.putString(KEY_SAVED_NEWS, value.joinToString(","))

    /**
     * Locally saved (bookmarked) marketplace item ids, comma-joined (ids never contain commas).
     * Device-local — peer listings are transient, so there's no account mirror like favorites have.
     */
    var savedMarketplaceItemIds: Set<String>
        get() = settings.getStringOrNull(KEY_SAVED_ITEMS)
            ?.split(',')?.filter { it.isNotBlank() }?.toSet() ?: emptySet()
        set(value) = if (value.isEmpty()) settings.remove(KEY_SAVED_ITEMS) else settings.putString(KEY_SAVED_ITEMS, value.joinToString(","))

    /**
     * Locally saved (bookmarked) Hidden Gems, comma-joined `type:id` entries (`joke:{id}` /
     * `recipe:{id}` — ids never contain commas). Guests stay device-local; signed-in accounts
     * union-merge with `users/{uid}/savedGems` via `GemsSavedController` (`:core:data`).
     */
    var savedGemIds: Set<String>
        get() = settings.getStringOrNull(KEY_SAVED_GEMS)
            ?.split(',')?.filter { it.isNotBlank() }?.toSet() ?: emptySet()
        set(value) = if (value.isEmpty()) settings.remove(KEY_SAVED_GEMS) else settings.putString(KEY_SAVED_GEMS, value.joinToString(","))

    /**
     * Device-local 18+ opt-in for the Hidden Gems adult jokes tier (pre-auth state). Signed-in
     * accounts also persist `users/{uid}.adultContentOptIn`; the effective opt-in is the OR of
     * both. Only meaningful while Remote Config `gems_adult_jokes_enabled` is on.
     */
    var gemsAdultOptIn: Boolean
        get() = settings.getBooleanOrNull(KEY_GEMS_ADULT_OPT_IN) ?: false
        set(value) = settings.putBoolean(KEY_GEMS_ADULT_OPT_IN, value)

    /**
     * Recent directory searches, newest first, capped at [MAX_RECENT_SEARCHES]. Joined with the
     * ASCII unit separator (never appears in typed queries, unlike commas). Device-local.
     */
    var recentSearches: List<String>
        get() = settings.getStringOrNull(KEY_RECENT_SEARCHES)
            ?.split(RECENT_SEPARATOR)?.filter { it.isNotBlank() } ?: emptyList()
        set(value) {
            val capped = value.filter { it.isNotBlank() }.distinct().take(MAX_RECENT_SEARCHES)
            if (capped.isEmpty()) settings.remove(KEY_RECENT_SEARCHES)
            else settings.putString(KEY_RECENT_SEARCHES, capped.joinToString(RECENT_SEPARATOR.toString()))
        }

    /** Businesses whose one-time "write a review?" nudge was already shown on this device. */
    var reviewNudgeShownIds: Set<String>
        get() = settings.getStringOrNull(KEY_REVIEW_NUDGED)
            ?.split(',')?.filter { it.isNotBlank() }?.toSet() ?: emptySet()
        set(value) = if (value.isEmpty()) settings.remove(KEY_REVIEW_NUDGED) else settings.putString(KEY_REVIEW_NUDGED, value.joinToString(","))

    /** The pickup cart, JSON-encoded (see `:core:data` CartController — it owns the schema). */
    var cartJson: String?
        get() = settings.getStringOrNull(KEY_CART)
        set(value) = if (value.isNullOrBlank()) settings.remove(KEY_CART) else settings.putString(KEY_CART, value)

    /**
     * Highest Terms of Service version the user explicitly accepted (0 = never). The app gates
     * entry until this reaches [TermsPolicy.VERSION]; bumping the version re-prompts everyone.
     * [termsAcceptedAtEpochMs] records WHEN — keep both as the consent record.
     */
    var acceptedTermsVersion: Int
        get() = settings.getIntOrNull(KEY_TERMS_VERSION) ?: 0
        set(value) = settings.putInt(KEY_TERMS_VERSION, value)

    var termsAcceptedAtEpochMs: Long
        get() = settings.getLongOrNull(KEY_TERMS_AT) ?: 0L
        set(value) = settings.putLong(KEY_TERMS_AT, value)

    /**
     * Whether the first-run onboarding flow (language → value pager → city → interests) has been
     * completed or skipped on this device. Gates the start destination; the Developer Menu's
     * "Reset onboarding" clears it so QA can replay the flow.
     */
    var onboardingComplete: Boolean
        get() = settings.getBooleanOrNull(KEY_ONBOARDING_DONE) ?: false
        set(value) = settings.putBoolean(KEY_ONBOARDING_DONE, value)

    /**
     * Whether the one-time spotlight feature tour (coach marks over the main scaffold's controls)
     * has been completed or skipped on this device. Shown once after the first Home render;
     * replayable from Profile ("App tour") and the Developer Menu.
     */
    var featureTourComplete: Boolean
        get() = settings.getBooleanOrNull(KEY_FEATURE_TOUR_DONE) ?: false
        set(value) = settings.putBoolean(KEY_FEATURE_TOUR_DONE, value)

    /**
     * Set by Settings → "App tour" (and the Developer Menu) to ask the main scaffold to run the
     * spotlight tour on its next composition. The tour no longer auto-starts on first run
     * (2026-09-11): it was the fourth interruption before a new member saw a single business.
     */
    var featureTourRequested: Boolean
        get() = settings.getBooleanOrNull(KEY_FEATURE_TOUR_REQUESTED) ?: false
        set(value) = settings.putBoolean(KEY_FEATURE_TOUR_REQUESTED, value)

    /**
     * Whether this device already showed the one-time "you now manage a business" welcome that
     * appears the first time the signed-in account owns a listing (claim approved or listing created).
     */
    var ownerWelcomeShown: Boolean
        get() = settings.getBooleanOrNull(KEY_OWNER_WELCOME_SHOWN) ?: false
        set(value) = settings.putBoolean(KEY_OWNER_WELCOME_SHOWN, value)

    /** Set by the owner welcome's "Show me around" so the dashboard runs its spotlight tour on open. */
    var ownerTourRequested: Boolean
        get() = settings.getBooleanOrNull(KEY_OWNER_TOUR_REQUESTED) ?: false
        set(value) = settings.putBoolean(KEY_OWNER_TOUR_REQUESTED, value)

    /**
     * Category ids the user picked as interests (onboarding or Profile), comma-joined (category ids
     * never contain commas). Personalizes Home ordering; mirrors to `users/{uid}.interests`.
     */
    var interestCategoryIds: Set<String>
        get() = settings.getStringOrNull(KEY_INTERESTS)
            ?.split(',')?.filter { it.isNotBlank() }?.toSet() ?: emptySet()
        set(value) = if (value.isEmpty()) settings.remove(KEY_INTERESTS) else settings.putString(KEY_INTERESTS, value.joinToString(","))

    /**
     * Whether the user has consented to usage analytics. **Default false** — Quebec Law 25 requires
     * privacy-by-default, so behavioural analytics stays OFF until the user opts in (Profile → Privacy).
     * The consent-gated analytics tracker reads this on every event; crash reporting (app stability) is
     * separate and not gated on this.
     */
    var analyticsConsent: Boolean
        get() = settings.getBooleanOrNull(KEY_ANALYTICS_CONSENT) ?: false
        set(value) = settings.putBoolean(KEY_ANALYTICS_CONSENT, value)

    /**
     * The Add-Business wizard's saved draft, as JSON (see `:core:data` BusinessDraftStore). Text
     * fields only — picked images are process-local file refs that can't survive a restart. `null`
     * = no saved draft.
     */
    var businessDraftJson: String?
        get() = settings.getStringOrNull(KEY_BUSINESS_DRAFT)
        set(value) = if (value == null) settings.remove(KEY_BUSINESS_DRAFT) else settings.putString(KEY_BUSINESS_DRAFT, value)

    private companion object {
        const val MAX_RECENT_SEARCHES = 8
        const val RECENT_SEPARATOR = '\u001F'
        const val KEY_RECENT_SEARCHES = "app.recentSearches"
        const val KEY_LANGUAGE = "app.language"
        const val KEY_APPEARANCE = "app.appearance"
        const val KEY_METRO = "app.metro"
        const val KEY_COUNTRY = "app.country"
        const val KEY_FAVORITES = "app.favorites"
        const val KEY_SAVED_ITEMS = "app.savedMarketplaceItems"
        const val KEY_SAVED_NEWS = "app.savedNews"
        const val KEY_SAVED_GEMS = "app.savedGems"
        const val KEY_GEMS_ADULT_OPT_IN = "app.gemsAdultOptIn"
        const val KEY_CART = "app.cart"
        const val KEY_REVIEW_NUDGED = "app.reviewNudgeShown"
        const val KEY_TERMS_VERSION = "app.termsAcceptedVersion"
        const val KEY_TERMS_AT = "app.termsAcceptedAtMs"
        const val KEY_ANALYTICS_CONSENT = "app.analyticsConsent"
        const val KEY_ONBOARDING_DONE = "app.onboardingDone"
        const val KEY_FEATURE_TOUR_DONE = "app.featureTourDone"
        const val KEY_FEATURE_TOUR_REQUESTED = "feature_tour_requested"
        const val KEY_OWNER_WELCOME_SHOWN = "app.ownerWelcomeShown"
        const val KEY_OWNER_TOUR_REQUESTED = "app.ownerTourRequested"
        const val KEY_INTERESTS = "app.interests"
        const val KEY_BUSINESS_DRAFT = "app.businessDraft"
    }
}

/**
 * Current Terms of Service version. Bump when `terms_body` changes materially — every user is
 * re-prompted to accept on next launch (the consent gate in `App`).
 */
object TermsPolicy {
    const val VERSION = 2
}
